/**
 * Common types used throughout the application
 */

import React from 'react';

// Base entity interface
export interface BaseEntity {
  id: string;
  createdAt: string;
  updatedAt: string;
}

// API Response wrapper
export interface ApiResponse<T = unknown> {
  data: T;
  message?: string;
  success: boolean;
}

// API Error response
export interface ApiError {
  message: string;
  code?: string;
  details?: unknown;
}

// Pagination parameters
export interface PaginationParams {
  page?: number;
  limit?: number;
  offset?: number;
}

// Paginated response
export interface PaginatedResponse<T = unknown> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

// Sort parameters
export interface SortParams {
  field: string;
  direction: 'asc' | 'desc';
}

// Filter parameters
export interface FilterParams {
  [key: string]: unknown;
}

// Query parameters combining pagination, sorting, and filtering
export interface QueryParams extends PaginationParams {
  sort?: SortParams;
  filters?: FilterParams;
  search?: string;
}

// Enhanced User interface based on architecture guide
export interface User extends BaseEntity {
  // Basic Information
  email: string;
  firstName: string;
  lastName: string;
  middleName?: string;
  displayName?: string;
  profilePhotoUrl?: string;

  // Employment Information
  employeeId?: string;
  jobTitle?: string;
  department?: string;
  hireDate?: string;
  workPhone?: string;
  workEmail?: string;

  // Contact Information
  personalEmail?: string;
  mobilePhone?: string;
  homePhone?: string;
  emergencyContact?: Record<string, any>;

  // Professional Information
  certifications?: string[];
  specializations?: string[];
  languages?: string[];
  skills?: string[];

  // Preferences
  timezone?: string;
  locale?: string;
  notificationPreferences?: Record<string, any>;
  uiPreferences?: Record<string, any>;

  // Status
  status: UserStatus;
  emailVerified: boolean;
  emailVerifiedAt?: string;
  lastLoginAt?: string;
  lastActivityAt?: string;
  passwordChangedAt?: string;

  // Onboarding
  onboardingCompleted: boolean;
  onboardingCompletedAt?: string;
  onboardingStep?: string;

  // Metadata
  source: UserSource;
  externalId?: string;
  metadata?: Record<string, any>;

  // Organization
  organizationId: string;

  // Legacy fields for backward compatibility
  avatar?: string;
  isActive: boolean;
}

// User status enum
export enum UserStatus {
  INVITED = 'invited',
  PENDING_APPROVAL = 'pending_approval',
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  SUSPENDED = 'suspended',
  DEACTIVATED = 'deactivated'
}

// User source enum
export enum UserSource {
  MANUAL = 'manual',
  INVITATION = 'invitation',
  IMPORT = 'import',
  SSO = 'sso',
  API = 'api',
  EMAIL = 'email',
  OAUTH = 'oauth'
}

// Organization interface
export interface Organization extends BaseEntity {
  parentId?: string;
  type: OrganizationType;
  name: string;
  legalName?: string;
  taxId?: string;

  // Contact Information
  primaryEmail?: string;
  primaryPhone?: string;
  website?: string;

  // Address
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;

  // Business Information
  dealerCode?: string;
  manufacturerAffiliations?: string[];
  businessHours?: Record<string, any>;

  // Subscription
  subscriptionTier: SubscriptionTier;
  subscriptionStatus: SubscriptionStatus;
  subscriptionExpiresAt?: string;
  seatLimit?: number;

  // Settings
  settings?: Record<string, any>;
  features?: string[];
  branding?: Record<string, any>;

  // Status
  status: OrganizationStatus;
  onboardedAt?: string;
  suspendedAt?: string;

  // Metadata
  metadata?: Record<string, any>;
}

// Organization types
export enum OrganizationType {
  ENTERPRISE = 'enterprise',
  DEALER_GROUP = 'dealer_group',
  DEALERSHIP = 'dealership'
}

// Organization status
export enum OrganizationStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  SUSPENDED = 'suspended',
  PENDING = 'pending'
}

// Subscription tiers
export enum SubscriptionTier {
  STARTER = 'starter',
  PROFESSIONAL = 'professional',
  ENTERPRISE = 'enterprise'
}

// Subscription status
export enum SubscriptionStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  SUSPENDED = 'suspended',
  EXPIRED = 'expired',
  TRIAL = 'trial'
}

// Role interface
export interface Role extends BaseEntity {
  organizationId?: string;
  name: string;
  displayName: string;
  description?: string;

  /** Tenant this role applies to (from prod.user_roles). Omitted for public-schema/legacy. */
  tenantId?: string | null;

  // Role Type
  type: RoleType;
  systemRole?: SystemRole;

  // Hierarchy
  parentRoleId?: string;
  priority: number;

  // Settings
  isAssignable: boolean;
  requiresApproval: boolean;
  maxUsers?: number;
}

// Role types
export enum RoleType {
  SYSTEM = 'system',
  CUSTOM = 'custom'
}

// System roles based on architecture guide
export enum SystemRole {
  SUPER_ADMIN = 'super_admin',
  ORGANIZATION_OWNER = 'organization_owner',
  DEALERSHIP_ADMIN = 'dealership_admin',
  /** @deprecated Use FIXED_OPERATIONS_DIRECTOR; DB role is fixed_operations_director */
  FIELD_OPERATIONS_DIRECTOR = 'field_operations_director',
  FIXED_OPERATIONS_DIRECTOR = 'fixed_operations_director',
  SERVICE_MANAGER = 'service_manager',
  SERVICE_ADVISOR = 'service_advisor',
  LEAD_TECHNICIAN = 'lead_technician',
  TECHNICIAN = 'technician',
  VIEWER = 'viewer'
}

// Permission interface
export interface Permission extends BaseEntity {
  resource: string;
  action: string;
  name: string;
  description?: string;

  // Permission categorization
  category?: string;
  riskLevel: RiskLevel;

  // Constraints
  requiresMfa: boolean;
  requiresApproval: boolean;
}

// Risk levels
export enum RiskLevel {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical'
}

// User role assignment
export interface UserRoleAssignment extends BaseEntity {
  userId: string;
  roleId: string;
  organizationId: string;

  // Assignment details
  assignedBy?: string;
  assignedAt: string;
  expiresAt?: string;

  // Scope limitations
  scopeType?: ScopeType;
  scopeId?: string;

  // Status
  isActive: boolean;
}

// Scope types
export enum ScopeType {
  ORGANIZATION = 'organization',
  DEALERSHIP = 'dealership',
  DEPARTMENT = 'department',
  TEAM = 'team'
}

// Permission scope
export enum PermissionScope {
  SELF = 'self',
  TEAM = 'team',
  DEPARTMENT = 'department',
  DEALERSHIP = 'dealership',
  ORGANIZATION = 'organization',
  ALL = 'all'
}

// Team interface
export interface Team extends BaseEntity {
  organizationId: string;
  name: string;
  description?: string;
  department?: string;

  // Team lead
  leadUserId?: string;

  // Settings
  isActive: boolean;
  autoAssignEnabled: boolean;
  autoAssignRules?: Record<string, any>;
}

// Team member
export interface TeamMember extends BaseEntity {
  teamId: string;
  userId: string;
  role: TeamMemberRole;
  joinedAt: string;
  leftAt?: string;
}

// Team member roles
export enum TeamMemberRole {
  LEAD = 'lead',
  MEMBER = 'member',
  TRAINEE = 'trainee'
}

// User invitation
export interface UserInvitation extends BaseEntity {
  organizationId: string;
  email: string;
  firstName?: string;
  lastName?: string;

  // Pre-assigned information
  roleIds: string[];
  teamIds: string[];
  department?: string;
  managerId?: string;

  // Invitation metadata
  invitedBy: string;
  invitationToken: string;
  expiresAt: string;

  // Custom message
  welcomeMessage?: string;
  onboardingPreset?: string;

  // Status
  status: InvitationStatus;
  acceptedAt?: string;
  acceptedByUserId?: string;
}

// Invitation status
export enum InvitationStatus {
  PENDING = 'pending',
  ACCEPTED = 'accepted',
  EXPIRED = 'expired',
  REVOKED = 'revoked'
}

// User session
export interface UserSession extends BaseEntity {
  userId: string;
  sessionToken: string;
  refreshToken?: string;

  // Device information
  ipAddress?: string;
  userAgent?: string;
  deviceType?: string;
  deviceName?: string;

  // Location
  country?: string;
  region?: string;
  city?: string;

  // Session lifecycle
  lastActivityAt: string;
  expiresAt: string;
  revokedAt?: string;
  revokedReason?: string;
}

// User approval interface
export interface UserApproval extends BaseEntity {
  userId: string;
  email: string;
  fullName?: string;
  authProvider: string;
  status: 'pending' | 'approved' | 'rejected';
  requestedAt: string;
  approvedAt?: string;
  approvedBy?: string;
  rejectionReason?: string;
  notes?: string;
  lastActiveAt?: string;
}

// Audit log
export interface AuditLog extends BaseEntity {
  // Actor information
  userId?: string;
  organizationId?: string;
  impersonatedBy?: string;

  // Action details
  action: string;
  resourceType: string;
  resourceId?: string;
  resourceName?: string;

  // Change details
  oldValues?: Record<string, any>;
  newValues?: Record<string, any>;

  // Context
  ipAddress?: string;
  userAgent?: string;
  sessionId?: string;

  // Result
  success: boolean;
  errorMessage?: string;

  // Metadata
  metadata?: Record<string, any>;
}

// Legacy UserRole enum for backward compatibility
export enum LegacyUserRole {
  ADMIN = 'admin',
  USER = 'user',
  GUEST = 'guest'
}

// Loading states
export interface LoadingState {
  isLoading: boolean;
  error: string | null;
}

// Legacy UserRole type for backward compatibility
export type UserRole = LegacyUserRole;

// Form field types
export interface FormField<T = string> {
  value: T;
  error?: string;
  touched: boolean;
  required?: boolean;
}

// Form state
export interface FormState<T extends Record<string, unknown>> {
  fields: { [K in keyof T]: FormField<T[K]> };
  isValid: boolean;
  isSubmitting: boolean;
}

// Theme types
export type Theme = 'light' | 'dark' | 'system';

// Notification types
export interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message?: string;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

// Modal types
export interface ModalState {
  isOpen: boolean;
  component?: React.ComponentType<unknown>;
  props?: Record<string, unknown>;
  onClose?: () => void;
}

// Route types
export interface RouteConfig {
  path: string;
  component: React.ComponentType<unknown>;
  exact?: boolean;
  protected?: boolean;
  roles?: UserRole[];
}

// Component props with children
export interface ComponentWithChildren {
  children: React.ReactNode;
}

// Component props with className
export interface ComponentWithClassName {
  className?: string;
}

// Component props with both children and className
export interface ComponentWithChildrenAndClassName
  extends ComponentWithChildren,
    ComponentWithClassName {}

// Generic component props
export interface BaseComponentProps extends ComponentWithChildrenAndClassName {
  id?: string;
  'data-testid'?: string;
}

// Recall data interface for manual routing
export interface RecallData {
  vin: string;
  campaign_number: string;
  year: number;
  make: string;
  model: string;
  seller_name: string;
  dealer_street: string;
  dealer_city: string;
  dealer_region: string;
  dealer_postal_code: string;
  dealer_phone: string;
  dealer_latitude: number | null;
  dealer_longitude: number | null;
  recall_title: string;
  priority?: number;
  mobile_eligible?: boolean;
}
